import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useLocation } from 'react-router-dom';
import { Trophy, Lock, ChevronRight, Sun, Moon, Loader2 } from 'lucide-react';
import { API } from '../utils/api';

const QUEUE_TOKEN_KEY = 'polla_queue_token';
const BYPASS_PATHS = ['/monitor', '/queue-preview', '/admin'];

export function VirtualQueue({ children }) {
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const [isAllowed, setIsAllowed] = useState(null);
    const [position, setPosition] = useState(0);
    const [estimatedWait, setEstimatedWait] = useState(0);
    const [queueToken, setQueueToken] = useState(null);
    const [justGranted, setJustGranted] = useState(false);
    const wasInQueue = useRef(false);

    // Bypass queue for monitor/preview/admin pages
    const shouldBypass = BYPASS_PATHS.some(p => location.pathname.startsWith(p));

    useEffect(() => {
        let token = localStorage.getItem(QUEUE_TOKEN_KEY);
        if (!token) {
            token = `qt_${Math.random().toString(36).substring(2)}${Date.now().toString(36)}`;
            localStorage.setItem(QUEUE_TOKEN_KEY, token);
        }
        setQueueToken(token);
    }, []);

    const checkAccess = useCallback(async () => {
        if (!queueToken) return;
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 8000);

            const response = await fetch(`${API}/queue/check`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ queue_token: queueToken }),
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (!response.ok) throw new Error('Queue check failed');
            const data = await response.json();

            // If user was in queue and now is allowed → show "Todo listo"
            if (data.allowed && wasInQueue.current) {
                setJustGranted(true);
            }

            if (!data.allowed) {
                wasInQueue.current = true;
            }

            setIsAllowed(data.allowed);
            setPosition(data.position || 0);
            setEstimatedWait(data.estimated_wait || 0);

            if (data.queue_token && data.queue_token !== queueToken) {
                localStorage.setItem(QUEUE_TOKEN_KEY, data.queue_token);
                setQueueToken(data.queue_token);
            }
        } catch (err) {
            // On error: DON'T change isAllowed state
            // Only set to true on initial load (null) as fallback
            if (isAllowed === null) {
                console.error('Queue check initial error, allowing access:', err.message);
                setIsAllowed(true);
            }
            // If already in queue (isAllowed === false), STAY in queue
            // If already allowed (isAllowed === true), STAY allowed
            // This prevents the flickering bug
        }
    }, [queueToken, isAllowed]);

    const sendHeartbeat = useCallback(async () => {
        if (!queueToken) return;
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 8000);

            const response = await fetch(`${API}/queue/heartbeat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ queue_token: queueToken }),
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                // Only update if explicitly expired
                if (data.expired) {
                    localStorage.removeItem(QUEUE_TOKEN_KEY);
                    window.location.reload();
                    return;
                }
                // Don't set isAllowed from heartbeat to avoid flicker
                // Heartbeat just keeps the session alive
                setPosition(data.position || 0);
            }
        } catch (err) {
            // Ignore heartbeat errors silently
        }
    }, [queueToken]);

    // Initial check
    useEffect(() => {
        if (queueToken && !shouldBypass) checkAccess();
    }, [queueToken, shouldBypass]); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll while in queue - every 5s to reduce server load
    useEffect(() => {
        if (isAllowed === false && !shouldBypass) {
            const interval = setInterval(checkAccess, 5000);
            return () => clearInterval(interval);
        }
    }, [isAllowed, shouldBypass]); // eslint-disable-line react-hooks/exhaustive-deps

    // Heartbeat while allowed
    useEffect(() => {
        if (isAllowed === true && !justGranted && !shouldBypass) {
            const interval = setInterval(sendHeartbeat, 30000);
            return () => clearInterval(interval);
        }
    }, [isAllowed, justGranted, sendHeartbeat]);

    const isDark = theme === 'dark';

    // Bypass for monitor/preview/admin
    if (shouldBypass) return children;

    // Loading state
    if (isAllowed === null) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background" data-testid="queue-loading">
                <div className="mb-8 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${isDark ? 'bg-blue-600 shadow-blue-900/40' : 'bg-blue-600 shadow-blue-200'}`}>
                        <Trophy className="text-white w-6 h-6" strokeWidth={2.5} />
                    </div>
                    <h1 className="text-xl font-bold tracking-tight uppercase">
                        Polla <span className="text-blue-600">Remakia</span>
                    </h1>
                </div>
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                <p className="text-muted-foreground text-sm mt-3">Conectando...</p>
            </div>
        );
    }

    // Just granted access - show "Todo listo" screen
    if (justGranted) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background relative" data-testid="queue-granted">
                <button
                    onClick={toggleTheme}
                    className={`absolute top-5 right-5 w-10 h-10 rounded-xl border flex items-center justify-center transition-all hover:shadow-lg active:scale-90 ${isDark ? 'bg-slate-800 border-slate-700 hover:border-blue-500/50 hover:shadow-blue-900/20' : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-blue-100'}`}
                    data-testid="queue-theme-toggle"
                >
                    {isDark ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-500" />}
                </button>

                <div className="mb-12 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${isDark ? 'bg-blue-600 shadow-blue-900/40' : 'bg-blue-600 shadow-blue-200'}`}>
                        <Trophy className="text-white w-6 h-6" strokeWidth={2.5} />
                    </div>
                    <h1 className="text-xl font-bold tracking-tight uppercase">
                        Polla <span className="text-blue-600">Remakia</span>
                    </h1>
                </div>

                <div
                    className={`max-w-md w-full rounded-[2.5rem] p-10 text-center ${isDark ? 'bg-slate-800/90 border border-slate-700/80 shadow-xl shadow-blue-900/10' : 'bg-white/90 border border-slate-200/80 shadow-xl shadow-blue-100/50'}`}
                    style={{ backdropFilter: 'blur(10px)' }}
                    data-testid="queue-granted-card"
                >
                    <div className="mb-6">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase ${isDark ? 'bg-green-900/30 text-green-400 border border-green-800/50' : 'bg-green-50 text-green-600 border border-green-100'}`}>
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2" />
                            Acceso permitido
                        </span>
                    </div>
                    <h2 className="text-2xl font-bold mb-2">¡Todo listo!</h2>
                    <p className="text-muted-foreground text-sm mb-10 leading-relaxed">
                        Ya puedes acceder a la plataforma de predicciones.
                    </p>
                    <button
                        onClick={() => setJustGranted(false)}
                        className={`w-full font-bold py-4 px-8 rounded-2xl shadow-lg transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 text-lg ${isDark ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-900/30' : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200'}`}
                        data-testid="queue-enter-btn"
                    >
                        Entrar ahora
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>

                <div className="mt-10 text-center">
                    <p className="text-muted-foreground text-[11px] font-medium flex items-center justify-center gap-1.5 uppercase tracking-widest opacity-60">
                        <Lock className="w-3.5 h-3.5" />
                        Acceso Prioritario
                    </p>
                </div>
            </div>
        );
    }

    // Allowed directly - render children
    if (isAllowed) return children;

    // Waiting in queue
    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background relative" data-testid="queue-waiting">
            <button
                onClick={toggleTheme}
                className={`absolute top-5 right-5 w-10 h-10 rounded-xl border flex items-center justify-center transition-all hover:shadow-lg active:scale-90 ${isDark ? 'bg-slate-800 border-slate-700 hover:border-blue-500/50 hover:shadow-blue-900/20' : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-blue-100'}`}
                data-testid="queue-theme-toggle"
            >
                {isDark ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-500" />}
            </button>

            <div className="mb-12 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${isDark ? 'bg-blue-600 shadow-blue-900/40' : 'bg-blue-600 shadow-blue-200'}`}>
                    <Trophy className="text-white w-6 h-6" strokeWidth={2.5} />
                </div>
                <h1 className="text-xl font-bold tracking-tight uppercase">
                    Polla <span className="text-blue-600">Remakia</span>
                </h1>
            </div>

            <div
                className={`max-w-md w-full rounded-[2.5rem] p-10 text-center ${isDark ? 'bg-slate-800/90 border border-slate-700/80 shadow-xl shadow-blue-900/10' : 'bg-white/90 border border-slate-200/80 shadow-xl shadow-blue-100/50'}`}
                style={{ backdropFilter: 'blur(10px)' }}
                data-testid="queue-card"
            >
                <div className="mb-6">
                    <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase ${isDark ? 'bg-blue-900/30 text-blue-400 border border-blue-800/50' : 'bg-blue-50 text-blue-600 border border-blue-100'}`}
                        style={{ animation: 'pulse-soft 2s infinite ease-in-out' }}
                    >
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2" />
                        Fila en curso
                    </span>
                </div>

                <h2 className="text-2xl font-bold mb-2">Espera un momento</h2>
                <p className="text-muted-foreground text-sm mb-10 leading-relaxed">
                    Estamos validando los accesos al sistema. Tu turno está por llegar.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className={`rounded-2xl p-5 border ${isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-slate-50 border-slate-100'}`} data-testid="queue-position">
                        <p className={`text-[10px] uppercase font-bold mb-1 tracking-tight ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Tu Posición</p>
                        <p className="text-3xl font-bold text-blue-600 tracking-tighter">{position}</p>
                    </div>
                    <div className={`rounded-2xl p-5 border ${isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-slate-50 border-slate-100'}`} data-testid="queue-wait-time">
                        <p className={`text-[10px] uppercase font-bold mb-1 tracking-tight ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Espera Est.</p>
                        <p className="text-3xl font-bold tracking-tighter">
                            {estimatedWait < 60 ? '< 1 min' : `${Math.ceil(estimatedWait / 60)} min`}
                        </p>
                    </div>
                </div>
            </div>

            <div className="mt-10 text-center">
                <p className="text-muted-foreground text-[11px] font-medium flex items-center justify-center gap-1.5 uppercase tracking-widest opacity-60">
                    <Lock className="w-3.5 h-3.5" />
                    Acceso Prioritario
                </p>
            </div>

            <style>{`
                @keyframes pulse-soft {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.7; }
                }
            `}</style>
        </div>
    );
}

export default VirtualQueue;
